import { useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { adhdAppImage } from "@/assets";

const deckUrl = "https://thrivr-accountability.replit.app/thrivr-pitch-deck/";

export default function Thrivr() {
  useEffect(() => {
    document.title = "Thrivr Accountability App | Shateara Davis";
  }, []);

  return (
    <div className="flex flex-col pt-10">
      <section className="pt-20 pb-20 bg-background border-b border-border/40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <Link
            href="/projects"
            className="inline-block text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors mb-10"
          >
            ← Back to projects
          </Link>

          <div className="grid lg:grid-cols-[1.05fr_.95fr] gap-12 lg:gap-16 items-center">
            <div>
              <div className="flex flex-wrap gap-3 mb-7">
                <span className="border border-border/60 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary">
                  Product Design
                </span>
                <span className="border border-border/60 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  In Progress
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-serif text-foreground leading-[0.95] tracking-tight mb-8">
                Thrivr
                <span className="block text-primary italic">Accountability.</span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed max-w-2xl">
                A gamified accountability app designed for adults with ADHD—turning
                daily tasks into rewarding quests through motivation, behavioral
                design, and meaningful progress.
              </p>
            </div>

            <div className="aspect-[4/3] overflow-hidden border border-border/40 bg-muted">
              <img
                src={adhdAppImage}
                alt="Thrivr accountability app interface"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-accent/20 border-b border-border/40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="grid md:grid-cols-3 gap-10">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">The problem</p>
              <p className="text-foreground leading-relaxed">
                Traditional productivity systems often rely on pressure, repetition,
                and guilt—approaches that can work against neurodivergent motivation.
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">The approach</p>
              <p className="text-foreground leading-relaxed">
                Thrivr reframes responsibilities as quests, using rewards and visible
                progress to make follow-through feel engaging and achievable.
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">The vision</p>
              <p className="text-foreground leading-relaxed">
                Build an accountability experience that respects how ADHD minds work
                rather than asking users to force themselves into rigid systems.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
                Seed round · 2026
              </p>
              <h2 className="text-4xl md:text-5xl font-serif text-foreground">
                Explore the pitch deck.
              </h2>
            </div>
            <a href={deckUrl} target="_blank" rel="noopener noreferrer">
              <Button className="rounded-none bg-primary hover:bg-primary/90 text-white px-7 uppercase tracking-wider text-xs">
                Open full screen ↗
              </Button>
            </a>
          </div>

          <div className="relative w-full aspect-video overflow-hidden border border-border/60 shadow-sm bg-[#182016]">
            <iframe
              src={deckUrl}
              title="Thrivr seed round pitch deck"
              className="absolute inset-0 w-full h-full"
              loading="lazy"
              allowFullScreen
            />
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Use the controls inside the deck to move through the presentation.
          </p>
        </div>
      </section>
    </div>
  );
}